import { Chart } from "@highcharts/react";
import type { Point, SeriesClickEventObject } from "highcharts/highcharts.src";
import { Col, Row } from "react-bootstrap";
import { renderToString } from "react-dom/server";
import { trellisPalette } from "../../../../components/custom/analytics/palettes";
import { type ReactNode, useCallback, useMemo, useState } from "react";
import { MdRotateLeft } from "react-icons/md";
import Select from "react-select";
import PathToPurchaseTable from "./PathToPurchaseTable";
import { pathMapping } from "../../utils/pathToPurchase/utils";
import { type ConversionPathData } from "../../utils/pathToPurchase/types";
import Loading from "../../../../components/core/blocks/Loading";
import { formatCurrency, formatNumber } from "../../../../utils/formatNumber";
import { useAppSelector } from "../../../../redux/hooks";
import moment from "moment";
import { useAMCReportData } from "../../hooks/useAMCReportData";
import { css } from "../../../../../styled-system/css";
import { useTranslation } from "react-i18next";

interface Props {
  isLoading: boolean;
  reportData: [ConversionPathData];
  reportDetails: ReportMetadata;
}

interface MetricOption {
  label: string;
  value: string;
}

interface NodeLink {
  from: string;
  to: string;
  weight: string;
}

interface ReportMetadata {
  id: number;
  metadata: {
    timeWindowStart: string;
    timeWindowEnd: string;
  };
  parent?: number;
  subscription?: number | null;
}

interface FormattedReportMetadata {
  id: number;
  formattedStartDate: string;
  formattedEndDate: string;
}

const PathToPurchaseReport = ({ reportData, isLoading, reportDetails }: Props) => {
  const { t } = useTranslation(["amc"]);
  const metricOptions: MetricOption[] = useMemo(
    () => [
      { value: "impressions", label: t(($) => $.amc["Impressions"]) },
      { value: "purchases", label: t(($) => $.amc["Purchases"]) },

      { value: "ntb_purchases", label: t(($) => $.amc["NTB Purchases"]) },
      { value: "total_cost", label: t(($) => $.amc["Cost"]) },
      {
        value: "sales_amount",
        label: t(($) => $.amc["Sales"]),
      },
      {
        value: "ntb_sales_amount",
        label: t(($) => $.amc["NTB Sales"]),
      },
    ],
    [t],
  );

  const formatTableDescription = (nodeName: string) => {
    const lastChar: string = nodeName[nodeName.length - 1] ?? "";

    if (isNaN(parseInt(lastChar))) {
      return nodeName;
    }

    const [adType, touchpointIndex] = nodeName.split("-");

    if (!touchpointIndex) {
      return nodeName;
    }

    const touchpointDescriptionMap: Record<number, string> = {
      0: t(($) => $.amc["Initial"]),
      1: t(($) => $.amc["2nd"]),
      2: t(($) => $.amc["3rd"]),
      3: t(($) => $.amc["4th"]),
    };

    return t(($) => $.amc["{adType} as {touchpoint} Touchpoint"], {
      adType,
      touchpoint: touchpointDescriptionMap[parseInt(touchpointIndex)] ?? "",
    });
  };

  const [selectedMetricValue, setSelectedMetricValue] = useState("impressions");
  const selectedMetric = useMemo<MetricOption | null>(
    () => metricOptions.find((opt) => opt.value === selectedMetricValue) ?? metricOptions[0] ?? null,
    [metricOptions, selectedMetricValue],
  );
  const [selectedConversionNode, setSelectedConversionNode] = useState<string | null>(null);

  const { marketPlace } = useAppSelector((state) => state.marketPlace);

  const startDate = moment(reportDetails.metadata.timeWindowStart).format("YYYY-MM-DD");
  const endDate = moment(reportDetails.metadata.timeWindowEnd).format("YYYY-MM-DD");

  const { reportMetadata } = useAMCReportData(null, "PATH_TO_PURCHASE_CAMPAIGNS_INTERNAL", false, null, true, {});

  const campaignReportId = useMemo(() => {
    if (reportDetails.subscription) {
      return reportMetadata?.find((m: ReportMetadata) => m.parent === reportDetails.id)?.id;
    }

    const campaignReportMetadata = reportMetadata
      ?.map((meta: ReportMetadata) => ({
        id: meta.id,
        formattedStartDate: moment(meta.metadata.timeWindowStart).format("YYYY-MM-DD"),
        formattedEndDate: moment(meta.metadata.timeWindowEnd).format("YYYY-MM-DD"),
      }))
      ?.find((report: FormattedReportMetadata) => {
        return report.formattedStartDate === startDate && report.formattedEndDate === endDate;
      });

    return campaignReportMetadata?.id;
  }, [endDate, reportDetails, reportMetadata, startDate]);

  // Start fetching campaign report as soon as possible since it's a massive data set
  useAMCReportData(campaignReportId);

  const NODES = useMemo(
    () => [
      // 1. Total
      ["Total", "Display", 1],
      ["Total", "Search", 1],
      // 2. Audience Starts with SD/DSP, Path Starts With Search
      ["Display", "Sponsored Display-0", 1],
      ["Display", "DSP-0", 1],
      ["Search", "Sponsored Brand-0", 1],
      ["Search", "Sponsored Product-0", 1],
      // 3. Each ad type connects to other ad types in next node
      ["Sponsored Product-0", "Sponsored Display-1", 1],
      ["Sponsored Product-0", "DSP-1", 1],
      ["Sponsored Product-0", "Sponsored Brand-1", 1],
      ["Sponsored Display-0", "Sponsored Product-1", 1],
      ["Sponsored Display-0", "Sponsored Brand-1", 1],
      ["Sponsored Display-0", "DSP-1", 1],
      ["DSP-0", "Sponsored Product-1", 1],
      ["DSP-0", "Sponsored Brand-1", 1],
      ["DSP-0", "Sponsored Display-1", 1],
      ["Sponsored Brand-0", "Sponsored Display-1", 1],
      ["Sponsored Brand-0", "DSP-1", 1],
      ["Sponsored Brand-0", "Sponsored Product-1", 1],
      // 4. Each ad type connects to other ad types in next node
      ["Sponsored Product-1", "Sponsored Display-2", 1],
      ["Sponsored Product-1", "DSP-2", 1],
      ["Sponsored Product-1", "Sponsored Brand-2", 1],
      ["Sponsored Display-1", "Sponsored Product-2", 1],
      ["Sponsored Display-1", "Sponsored Brand-2", 1],
      ["Sponsored Display-1", "DSP-2", 1],
      ["DSP-1", "Sponsored Product-2", 1],
      ["DSP-1", "Sponsored Brand-2", 1],
      ["DSP-1", "Sponsored Display-2", 1],
      ["Sponsored Brand-1", "Sponsored Display-2", 1],
      ["Sponsored Brand-1", "DSP-2", 1],
      ["Sponsored Brand-1", "Sponsored Product-2", 1],
      // 5. Each ad type connects to other ad types in next node
      ["Sponsored Product-2", "Sponsored Display-3", 1],
      ["Sponsored Product-2", "DSP-3", 1],
      ["Sponsored Product-2", "Sponsored Brand-3", 1],
      ["Sponsored Display-2", "Sponsored Product-3", 1],
      ["Sponsored Display-2", "Sponsored Brand-3", 1],
      ["Sponsored Display-2", "DSP-3", 1],
      ["DSP-2", "Sponsored Product-3", 1],
      ["DSP-2", "Sponsored Brand-3", 1],
      ["DSP-2", "Sponsored Display-3", 1],
      ["Sponsored Brand-2", "Sponsored Display-3", 1],
      ["Sponsored Brand-2", "DSP-3", 1],
      ["Sponsored Brand-2", "Sponsored Product-3", 1],
    ],
    [],
  );

  const data = useMemo(() => {
    const nodeDestinationKeys = [];
    const nodeAccessorMap: { [key: string]: string[] } = {};

    for (let i = 0; i < NODES.length; i++) {
      // oxlint-disable-next-line no-unused-vars
      const [from, destination, _] = NODES[i] ?? [];
      nodeDestinationKeys.push(destination);
    }

    for (const key of nodeDestinationKeys) {
      if (key) {
        const accessors = [];
        const adNumber = key[(key.toString().length - 1) as keyof object];

        if (!isNaN(parseInt(adNumber))) {
          accessors.push(
            `[${parseInt(adNumber) + 1}, ${pathMapping[key.toString().split("-")[0] as keyof typeof pathMapping]}-Others]`,
          );
        }

        nodeAccessorMap[key] = accessors;
      }
    }

    const accData: { [key: string]: ConversionPathData } = {};

    for (const key of nodeDestinationKeys) {
      if (!isNaN(parseInt(key!.toString().slice(-1)))) {
        const rowsToAggregate = reportData.filter((row: ConversionPathData) => {
          const accessorKeys = nodeAccessorMap[key!];
          return accessorKeys && accessorKeys.some((key: string) => row.path.endsWith(`${key}]`));
        });

        const aggData: ConversionPathData = rowsToAggregate.reduce(
          (acc: ConversionPathData, row: ConversionPathData) => {
            return {
              path: row.path,
              impressions: acc.impressions ? acc.impressions + row.impressions : row.impressions,
              ntb_purchases: acc.ntb_purchases ? acc.ntb_purchases + row.ntb_purchases : row.ntb_purchases,
              ntb_sales_amount:
                acc.ntb_sales_amount ? acc.ntb_sales_amount + row.ntb_sales_amount : row.ntb_sales_amount,
              ntb_users_that_purchased:
                acc.ntb_users_that_purchased ?
                  acc.ntb_users_that_purchased + row.ntb_users_that_purchased
                : row.ntb_users_that_purchased,
              path_occurrences:
                acc.path_occurrences ? acc.path_occurrences + row.path_occurrences : row.path_occurrences,
              purchases: acc.purchases ? acc.purchases + row.purchases : row.purchases,
              sales_amount: acc.sales_amount ? acc.sales_amount + row.sales_amount : row.sales_amount,
              total_cost:
                acc.total_cost ?
                  acc.total_cost + parseFloat(row.total_cost.toString())
                : parseFloat(row.total_cost.toString()),
              users_that_purchased:
                acc.users_that_purchased ?
                  acc.users_that_purchased + row.users_that_purchased
                : row.users_that_purchased,
            };
          },
          {
            path: "",
            path_occurrences: 0,
            impressions: 0,
            total_cost: 0,
            users_that_purchased: 0,
            sales_amount: 0,
            purchases: 0,
            user_purchase_rate: 0,
            ntb_users_that_purchased: 0,
            ntb_sales_amount: 0,
            ntb_purchases: 0,
            ntb_percentage: 0,
          },
        );

        accData[key!] = aggData;
      }
    }

    return accData;
  }, [NODES, reportData]);

  const tableData = useMemo(() => {
    if (!selectedConversionNode || selectedConversionNode === "Total") {
      return reportData.filter((row: ConversionPathData) => row.path.length);
    }

    const adNumber = selectedConversionNode[selectedConversionNode.length - 1];

    const pathMapping: { [key: string]: string } = {
      "Sponsored Display": "SD",
      "Sponsored Brand": "SB",
      "Sponsored Product": "SP",
      DSP: "DSP",
    };

    if (selectedConversionNode === "Search") {
      return reportData.filter((row: ConversionPathData) => {
        return row.path.startsWith(`[[1, SB`) || row.path.startsWith("[[1, SP");
      });
    }

    if (selectedConversionNode === "Display") {
      return reportData.filter((row: ConversionPathData) => {
        return row.path.startsWith("[[1, DSP") || row.path.startsWith("[[1, SD");
      });
    }

    if (adNumber && !isNaN(parseInt(adNumber))) {
      const conversionNodeKey: string | undefined = selectedConversionNode.split("-")[0];
      const endNodeValue = pathMapping[conversionNodeKey!];
      const endNodeAccessor = `[${parseInt(adNumber) + 1}, ${endNodeValue}-Others]`;

      return reportData.filter((row: ConversionPathData) => {
        return row.path.endsWith(`${endNodeAccessor}]`) || row.path.includes(endNodeAccessor);
      });
    }

    return [];
  }, [selectedConversionNode, reportData]);

  const getPathWeight = useCallback(
    (to: string, from: string): number => {
      const parseNode = (node: string, isDSP: boolean): string => {
        const index = parseInt(node[node.length - 1] ?? "") + 1;
        if (isDSP) {
          return `${index}, DSP-Others`;
        }
        const firstAdTypeChar = node.split(" ")[0]?.[0];
        const secondaryAdTypeChar = node.split(" ")[1]?.[0];
        return `${index}, ${firstAdTypeChar}${secondaryAdTypeChar}-Others`;
      };

      const generatePath = (fromNode: string, toNode: string): string => {
        const fromIsDSP = fromNode.includes("DSP");
        const toIsDSP = toNode.includes("DSP");

        if (!/\d+/g.test(fromNode)) {
          if (toIsDSP) {
            return `[[${parseNode(toNode, true)}]]`;
          }
          return `[[${parseNode(toNode, false)}]]`;
        }

        if (fromIsDSP && toIsDSP) {
          return `${parseNode(fromNode, true)}], [${parseNode(toNode, true)}]]`;
        }

        if (fromIsDSP) {
          return `${parseNode(fromNode, true)}], [${parseNode(toNode, false)}]]`;
        }

        if (toIsDSP) {
          return `${parseNode(fromNode, false)}], [${parseNode(toNode, true)}]]`;
        }

        return `${parseNode(fromNode, false)}], [${parseNode(toNode, false)}]]`;
      };

      // Get all rows with to and from nodes in path and sum up selectedMetric values
      const rows = reportData.filter((row: ConversionPathData) => {
        const path = generatePath(from, to);

        return row.path.includes(path.slice(0, -1));
      });

      return rows.reduce((acc: number, row: ConversionPathData) => {
        const metricValue = row[selectedMetric!.value as keyof ConversionPathData];

        if (typeof metricValue === "number") {
          return acc + metricValue;
        }

        return acc;
      }, 0);
    },
    [reportData, selectedMetric],
  );

  const renderNodeLabel = (markup: ReactNode) => {
    return renderToString(markup);
  };

  const chartOptions = useMemo(
    () => ({
      title: {
        text: "",
      },
      chart: {
        height: 520,
      },
      tooltip: {
        enabled: true,
        followPointer: true,
        followTouchMove: true,
        useHTML: true,
        headerFormat: "",
        padding: 0,
        distance: 40,
        shadow: false,
        nodeFormatter: function (): string {
          // @ts-expect-error circle back to type this out
          const { linksFrom, linksTo, sum } = this;
          const outgoingMetricTotal = linksFrom?.reduce((acc: number, link: NodeLink) => {
            return acc + link.weight;
          }, 0);
          const difference = sum - outgoingMetricTotal;

          if (sum < 1) {
            return "";
          }

          return renderToString(
            <div className="border p-3 fs-5" style={{ background: "#FFF", width: "350px" }}>
              <Row>
                <Col xs={4}>
                  <span className="fw-bold">{t(($) => $.amc["Incoming"])}</span>
                  <ul>
                    {linksTo
                      ?.filter((link: NodeLink) => {
                        return parseFloat(link.weight) >= 1;
                      })
                      ?.map((link: NodeLink, i: number) => {
                        return (
                          <li key={i} className="text-wrap">
                            {link.from.split("-")[0]}:
                            {["total_cost", "sales_amount", "ntb_sales_amount"].includes(selectedMetric!.value) ?
                              formatCurrency(parseFloat(link.weight), {
                                marketPlace: marketPlace,
                                decimalPlaces: 0,
                              })
                            : formatNumber(link.weight)}
                          </li>
                        );
                      })}
                  </ul>
                </Col>

                <Col className="text-center" xs={4}>
                  <span className="fw-bold">{t(($) => $.amc["Total"])}</span>
                  <br />
                  {["total_cost", "sales_amount", "ntb_sales_amount"].includes(selectedMetric!.value) ?
                    formatCurrency(parseFloat(sum), {
                      marketPlace: marketPlace,
                      decimalPlaces: 0,
                    })
                  : formatNumber(sum)}
                  <br />
                  {difference ?
                    <>
                      <span className="fw-bold pt-3">{t(($) => $.amc["Difference"])}</span>
                      <br />
                      {["total_cost", "sales_amount", "ntb_sales_amount"].includes(selectedMetric!.value) ?
                        formatCurrency(difference, {
                          marketPlace: marketPlace,
                          decimalPlaces: 0,
                        })
                      : formatNumber(difference)}
                    </>
                  : null}
                </Col>
                <Col xs={4}>
                  <span className="fw-bold">{t(($) => $.amc["Outgoing"])}</span>
                  <ul>
                    {linksFrom
                      ?.filter((link: NodeLink) => {
                        return parseFloat(link.weight) >= 1;
                      })
                      .map((link: NodeLink, i: number) => {
                        return (
                          <li key={i} className="text-wrap">
                            {link.to.split("-")[0]}:{" "}
                            {["total_cost", "sales_amount", "ntb_sales_amount"].includes(selectedMetric!.value) ?
                              formatCurrency(parseFloat(link.weight), {
                                marketPlace: marketPlace,
                                decimalPlaces: 0,
                              })
                            : formatNumber(link.weight)}
                          </li>
                        );
                      })}
                  </ul>
                </Col>
              </Row>
            </div>,
          );
        },
        pointFormatter: function (): string {
          // @ts-expect-error circle back to type this out
          const { from, to, weight } = this;

          if (weight < 1) {
            return "";
          }
          return renderToString(
            <div className="border p-3 fs-5" style={{ background: "#FFF", width: "350px" }}>
              <span className="me-1">
                {t(($) => $.amc["{from} to {to}:"], { from: from.split("-")[0], to: to.split("-")[0] })}
              </span>
              <span className="fw-bold">
                {["total_cost", "sales_amount", "ntb_sales_amount"].includes(selectedMetric!.value) ?
                  formatCurrency(weight, {
                    marketPlace: marketPlace,
                  })
                : formatNumber(weight)}
              </span>
            </div>,
          );
        },
      },
      plotOptions: {
        sankey: {
          curveFactor: 0.5,
          nodePadding: 150,
        },
      },
      series: [
        {
          dataLabels: {
            useHTML: true,
            nodeFormatter: function (this: Point): string {
              const node = this as Point & { id: string; sum: number };
              let displayName = node.name;

              if (node.name === "Total") {
                displayName = t(($) => $.amc["Total {label}"], { label: selectedMetric?.label ?? "" });
              }
              return `
          <div style="background: ${node.sum < 1 ? "#EEE" : "#FFF"}; padding: 10px; border:  ${node.id === selectedConversionNode ? "2px solid " + trellisPalette[7] : "1px solid #CCC"}; height: 70px; display: flex; align-items: center;" >
          <span style="font-weight: normal; font-size: 13px; color: #333; font-weight: ${node.id === selectedConversionNode ? 500 : 400};">${displayName}</span>

          </div>
          `;
            },

            style: {
              fontFamily: "Poppins",
              fontWeight: "500",
            },
          },
          cursor: "pointer",
          events: {
            click: (e: SeriesClickEventObject) => {
              e.preventDefault();
              const node = e.point as Point & { id: string; sum: number };

              if (node.sum < 1) return;

              if (node.id === selectedConversionNode) {
                setSelectedConversionNode(null);
              } else {
                setSelectedConversionNode(node.id);
              }
            },
            legendItemClick: function () {
              return false;
            },
          },
          states: {
            inactive: {
              opacity: 1,
            },
          },

          allowPointSelect: false,
          nodes: [
            {
              id: "Total",
              color: trellisPalette[7],
            },
            {
              id: "Display",
              color: trellisPalette[7],
            },
            {
              id: "Search",
              color: trellisPalette[7],
            },
            {
              id: "Sponsored Display-0",
              color: trellisPalette[7],
              name: renderNodeLabel(
                <div className="text-center">
                  {t(($) => $.amc["Sponsored Display"])} <br />
                  <small>{t(($) => $.amc["Initial Touchpoint"])}</small>
                </div>,
              ),
            },
            {
              id: "Sponsored Product-0",
              color: trellisPalette[7],
              name: renderNodeLabel(
                <div className="text-center">
                  {t(($) => $.amc["Sponsored Product"])} <br />
                  <small>{t(($) => $.amc["Initial Touchpoint"])}</small>
                </div>,
              ),
            },
            {
              id: "Sponsored Brand-0",
              color: trellisPalette[7],
              name: renderNodeLabel(
                <div className="text-center">
                  {t(($) => $.amc["Sponsored Brand"])} <br />
                  <small>{t(($) => $.amc["Initial Touchpoint"])}</small>
                </div>,
              ),
            },
            {
              id: "DSP-0",
              color: trellisPalette[7],
              name: renderNodeLabel(
                <div className="text-center">
                  {t(($) => $.amc["DSP"])} <br />
                  <small>{t(($) => $.amc["Initial Touchpoint"])}</small>
                </div>,
              ),
            },
            {
              id: "Sponsored Display-1",
              color: trellisPalette[7],
              name: renderNodeLabel(
                <div className="text-center">
                  {t(($) => $.amc["Sponsored Display"])}
                  <br />
                  <small>{t(($) => $.amc["Second Touchpoint"])}</small>
                </div>,
              ),
            },
            {
              id: "Sponsored Product-1",
              color: trellisPalette[7],
              name: renderNodeLabel(
                <div className="text-center">
                  {t(($) => $.amc["Sponsored Product"])}
                  <br />
                  <small>{t(($) => $.amc["Second Touchpoint"])}</small>
                </div>,
              ),
            },
            {
              id: "Sponsored Brand-1",
              color: trellisPalette[7],
              name: renderNodeLabel(
                <div className="text-center">
                  {t(($) => $.amc["Sponsored Brand"])}
                  <br />
                  <small>{t(($) => $.amc["Second Touchpoint"])}</small>
                </div>,
              ),
            },
            {
              id: "DSP-1",
              color: trellisPalette[7],
              name: renderNodeLabel(
                <div className="text-center">
                  {t(($) => $.amc["DSP"])}
                  <br />
                  <small>{t(($) => $.amc["Second Touchpoint"])}</small>
                </div>,
              ),
            },
            {
              id: "Sponsored Display-2",
              color: trellisPalette[7],
              name: renderNodeLabel(
                <div className="text-center">
                  {t(($) => $.amc["Sponsored Display"])}
                  <br />
                  <small>{t(($) => $.amc["Third Touchpoint"])}</small>
                </div>,
              ),
            },
            {
              id: "Sponsored Product-2",
              color: trellisPalette[7],
              name: renderNodeLabel(
                <div className="text-center">
                  {t(($) => $.amc["Sponsored Product"])}
                  <br />
                  <small>{t(($) => $.amc["Third Touchpoint"])}</small>
                </div>,
              ),
            },
            {
              id: "Sponsored Brand-2",
              color: trellisPalette[7],
              name: renderNodeLabel(
                <div className="text-center">
                  {t(($) => $.amc["Sponsored Brand"])}
                  <br />
                  <small>{t(($) => $.amc["Third Touchpoint"])}</small>
                </div>,
              ),
            },
            {
              id: "DSP-2",
              color: trellisPalette[7],
              name: renderNodeLabel(
                <div className="text-center">
                  {t(($) => $.amc["DSP"])}
                  <br />
                  <small>{t(($) => $.amc["Third Touchpoint"])}</small>
                </div>,
              ),
            },
            {
              id: "Sponsored Display-3",
              color: trellisPalette[7],
              name: renderNodeLabel(
                <div className="text-center">
                  {t(($) => $.amc["Sponsored Display"])}
                  <br />
                  <small>{t(($) => $.amc["Fourth Touchpoint"])}</small>
                </div>,
              ),
            },
            {
              id: "Sponsored Product-3",
              color: trellisPalette[7],
              name: renderNodeLabel(
                <div className="text-center">
                  {t(($) => $.amc["Sponsored Product"])}
                  <br />
                  <small>{t(($) => $.amc["Fourth Touchpoint"])}</small>
                </div>,
              ),
            },
            {
              id: "Sponsored Brand-3",
              color: trellisPalette[7],
              name: renderNodeLabel(
                <div className="text-center">
                  {t(($) => $.amc["Sponsored Brand"])}
                  <br />
                  <small>{t(($) => $.amc["Fourth Touchpoint"])}</small>
                </div>,
              ),
            },
            {
              id: "DSP-3",
              color: trellisPalette[7],
              name: renderNodeLabel(
                <div className="text-center">
                  {t(($) => $.amc["DSP"])}
                  <br />
                  <small>{t(($) => $.amc["Fourth Touchpoint"])}</small>
                </div>,
              ),
            },
          ],
          keys: ["from", "to", "weight", "color", "linkOpacity"],
          data: NODES.map((node) => {
            let [from, to, value] = node as [string, string, number];
            let color = trellisPalette[7];
            let linkOpacity = 0.5;

            const lastChar = to[to.length - 1];

            if (data[to] && lastChar && selectedMetric && !isNaN(parseFloat(lastChar))) {
              const selectedMetricValue = getPathWeight(to, from);

              if (typeof selectedMetricValue === "number") {
                value = selectedMetricValue;
              }
            }

            // These get the total conversion metric value for all nodes that end on SB or SP
            if (to === "Search") {
              // Get total for SB/SP impressions

              const rows = reportData.filter(
                (row: ConversionPathData) => row.path.includes("[1, SB-Others]") || row.path.includes("[1, SP-Others]"),
              );

              value = rows.reduce((acc: number, row: ConversionPathData) => {
                const rowValue = row[selectedMetric?.value as keyof ConversionPathData];
                if (typeof rowValue === "number") {
                  return acc + rowValue;
                }
                return acc;
              }, 0);
            }

            // These get the total conversion metric value for all nodes that end on SD or DSP
            if (to === "Display") {
              const rows = reportData.filter(
                (row: ConversionPathData) =>
                  row.path.includes("[1, SD-Others]") || row.path.includes("[1, DSP-Others]"),
              );
              value = rows.reduce((acc: number, row: ConversionPathData) => {
                const selectedMetricValue = row[selectedMetric?.value as keyof ConversionPathData];
                if (selectedMetricValue && typeof selectedMetricValue === "number") {
                  return acc + selectedMetricValue;
                }
                return acc;
              }, 0);
            }
            // This is a hack to avoid highcharts automatically removing nodes with no value
            if (!value) {
              value = 0.1;
              color = "transparent";
            }

            if (selectedConversionNode && [to, from].includes(selectedConversionNode)) {
              linkOpacity = 1;
            } else if (selectedConversionNode) {
              linkOpacity = 0.02;
            }

            return [from, to, value, color, linkOpacity];
          }),
          minLinkWidth: 1,
          type: "sankey" as const,
        },
      ],
    }),
    [NODES, data, selectedConversionNode, selectedMetric, getPathWeight, marketPlace, reportData, t],
  );

  return (
    <div style={{ minHeight: "calc(100vh - 150px" }}>
      {isLoading ?
        <Loading fullPage={true} />
      : <>
          <Col className="d-flex justify-content-end pb-3 pe-4" xs={12}>
            <div style={{ width: "400px" }}>
              <Select
                options={metricOptions}
                value={selectedMetric}
                onChange={(newValue) => {
                  if (newValue) {
                    setSelectedMetricValue(newValue.value);
                  }
                }}
              />
            </div>
          </Col>
          <Col className="pb-5 conversion-chart-container" xs={12}>
            <Chart options={chartOptions} />
          </Col>
          <Col className="my-5" xs={12}>
            <PathToPurchaseTable
              tableData={tableData}
              campaignReportId={campaignReportId}
              scopeDescription={
                <p
                  className="text-center"
                  style={{
                    position: "absolute",
                    left: 300,
                    right: 300,
                    fontWeight: 500,
                  }}
                >
                  {t(($) => $.amc["Showing Data for"])}{" "}
                  <span>
                    {selectedConversionNode ?
                      formatTableDescription(selectedConversionNode)
                    : t(($) => $.amc["All Conversions Paths"])}
                  </span>{" "}
                  {selectedConversionNode ?
                    <MdRotateLeft
                      className={css({
                        cursor: "pointer",
                        marginLeft: "auto",
                        fontSize: "24px",
                        fontWeight: "bold",
                      })}
                      onClick={() => setSelectedConversionNode(null)}
                    />
                  : null}
                </p>
              }
            />
          </Col>
        </>
      }
    </div>
  );
};

export default PathToPurchaseReport;
